import { Component } from '@theme/component';
import { openBackInStockDrawer, cancelBackInStockDrawer } from '@theme/back-in-stock-opener';

// Pulled in so the custom element the opener inserts is defined by the time it is upgraded.
// Imported here rather than in the theme bundle: the drawer is only ever needed on a page
// that shows this button, and the bundle is served on every page.
import '@theme/back-in-stock-drawer';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The CTA that opens the drawer.
 *
 * @extends Component<Refs>
 */
export class BackInStockButtonComponent extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.refs.button.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // The button is replaced wholesale every time the section re-renders on a variant change.
    // Without this abort each render would leave a listener behind, and one tap would fetch
    // the drawer as many times as the visitor has changed size.
    this.#abortController.abort();
    cancelBackInStockDrawer(this);
  }

  /**
   * @param {MouseEvent} event - The click on the CTA.
   */
  #handleClick = async (event) => {
    event.preventDefault();

    const { productUrl, variantId } = this.dataset;
    if (!productUrl || !variantId) return;

    this.#setBusy(true);

    const status = await openBackInStockDrawer({ productUrl, variantId, owner: this });

    // Cancelled is not somebody else's doing often enough to skip the reset: the two CTAs on a
    // product page -- the sticky bar and the one in the buy buttons block -- cancel each other,
    // and the loser would be left greyed out for good, since a disabled button cannot be
    // tapped again. The one cancellation that must not reset anything is the button's own, in
    // `disconnectedCallback`, and a disconnected button has no state left to clear anyway.
    if (this.isConnected) this.#setBusy(false);

    // `unavailable` means the variant turned out to be buyable after all -- the stock came
    // back between the page render and the tap. Sending the visitor to the product page is
    // the only honest answer: the page they are looking at is out of date.
    if (status === 'unavailable') window.location.assign(productUrl);
  };

  /**
   * @param {boolean} busy - Whether the drawer is on its way.
   */
  #setBusy(busy) {
    const { button } = this.refs;

    // Same attribute the add to cart button uses, so the spinner in
    // snippets/add-to-cart-button.liquid works here with no styles of its own.
    button.toggleAttribute('data-busy', busy);
    button.disabled = busy;
  }
}

if (!customElements.get('back-in-stock-button')) {
  customElements.define('back-in-stock-button', BackInStockButtonComponent);
}
